import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, switchMap } from 'rxjs';
import { Duration, intervalToDuration } from 'date-fns';
import { ICallState, IConversation } from '@dxp/core/models';
import { CustomerApiService, VoiceApiService } from '@dxp/core/api';
import { HeartbeatService, SipService, VoiceService } from '@dxp/core/services';

@Injectable({ providedIn: 'root' })
export class VoiceCallService {
    readonly #sipService = inject(SipService);
    readonly #voiceService = inject(VoiceService);
    readonly #heartbeatService = inject(HeartbeatService);
    readonly #customerApiService = inject(CustomerApiService);
    readonly #voiceApiService = inject(VoiceApiService);
    readonly #destroyRef = inject(DestroyRef);

    readonly transfer = signal(false);
    readonly enquiry = signal(false);
    readonly ivr = signal(false);
    readonly keypad = signal(false);
    readonly customerName = signal('Unknown');

    private readonly _now = this.#heartbeatService.lastHeartbeat;

    readonly conversation = computed<IConversation | undefined>(() => {
        const workItem = this.#voiceService.workItem();
        return workItem?.conversations.find(
            c => c.conversationId === workItem.primaryConversationId,
        );
    });

    constructor() {
        // Properly manage customer data subscription to avoid memory leak
        toObservable(this.#voiceService.workItem)
            .pipe(
                distinctUntilChanged(
                    (prev, curr) => prev?.customerId === curr?.customerId,
                ),
                filter(workItem => !!workItem?.customerId),
                switchMap(workItem =>
                    this.#customerApiService.getCustomer(workItem?.customerId!, [
                        'PhoneNumbers',
                        'ExternalProfiles',
                        'CustomFields',
                    ]),
                ),
                takeUntilDestroyed(this.#destroyRef),
            )
            .subscribe(customer =>
                this.customerName.set(customer.name ?? 'Unknown'),
            );
    }

    readonly callDescription = computed(() => {
        const workItem = this.#voiceService.workItem();
        return workItem?.isEnquiryCall
            ? 'Enquiry Call'
            : workItem?.isOutbound
              ? 'Outbound Call'
              : 'Inbound Call';
    });

    readonly callTime = computed<Duration>(() => {
        const workItem = this.#voiceService.workItem();
        return workItem
            ? intervalToDuration({
                  start: workItem.lastStateChangeDate,
                  end: this._now(),
              })
            : { hours: 0, minutes: 0, seconds: 0 };
    });

    endCall(): void {
        const workItem = this.#voiceService.workItem();
        if (workItem) {
            const params: ICallState = {
                conversationId: workItem.primaryConversationId as string,
                callState: 'Hangup',
            };
            this.#voiceApiService.changeCallState(params);
            this.#sipService.hangUp();
        }
        this.#voiceService.inCall = false;
    }

    toggleTransfer() {
        const showing = this.#voiceService.displayContacts;
        this.#voiceService.setContacts(
            !showing,
            showing ? '' : 'Transfer Call',
            showing ? '' : 'voice',
            showing ? undefined : 'voice-transfer',
            false,
        );
        this.transfer.set(!showing);
        this.enquiry.set(false);
        this.ivr.set(false);
    }

    toggleEnquiry() {
        const showing = this.#voiceService.displayContacts;
        this.#voiceService.setContacts(
            !showing,
            showing ? '' : 'Enquiry Call',
            showing ? '' : 'voice',
            showing ? undefined : 'voice-enquiry',
            false,
        );
        this.enquiry.set(!showing);
        this.transfer.set(false);
        this.ivr.set(false);
    }

    toggleIVR() {
        const showing = this.#voiceService.displayContacts;
        this.#voiceService.setContacts(
            !showing,
            showing ? '' : 'Return to IVR',
            showing ? '' : 'voice',
            showing ? undefined : 'voice-ivr',
            false,
        );
        this.ivr.set(!showing);
        this.transfer.set(false);
        this.enquiry.set(false);
    }

    sendDtmf(digit: string) {
        this.#sipService.sendDtmf(digit);
    }

    handleButtonClick(title: string) {
        switch (title) {
            case 'Transfer':
                this.toggleTransfer();
                break;
            case 'Enquiry':
                this.toggleEnquiry();
                break;
            case 'Return to IVR':
                this.toggleIVR();
                break;
        }
    }
}
